import { Alert, Button, Dialog, Select } from "@vellum/ui";
import type {
  DashboardSubscriptionSchema,
  SubscriptionAutoRecipientSchema,
  SubscriptionContactSchema,
  SubscriptionType,
} from "@vellum/vellum-api";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { vellumApiSdk } from "@vellum/micro-app-auth";

import { formatSendHourCT, SEND_DELAY_DAYS } from "./constants";

interface SubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (subscription: DashboardSubscriptionSchema | null) => void;
  typ: SubscriptionType;
  companyId: string;
  facilityId?: string;
  /** Metabase question the subscription's emailed CSV is rendered from. */
  metabaseReportId: number;
  subscription?: DashboardSubscriptionSchema | null;
}

interface FormValues {
  to_emails: string[];
  send_hour: number;
}

interface SelectOption {
  label: string;
  value: string;
}

const validationSchema = Yup.object().shape({
  to_emails: Yup.array()
    .of(Yup.string().email("Invalid email").required("Required"))
    .min(1, "Add at least one recipient is required"),
  send_hour: Yup.number().required("Required").min(0).max(23),
});

const sendHourOptions = Array.from({ length: 24 }, (_, hour) => ({
  label: formatSendHourCT(hour),
  value: hour,
}));

export function SubscriptionDialog({
  isOpen,
  onClose,
  onSaved,
  typ,
  companyId,
  facilityId,
  metabaseReportId,
  subscription,
}: SubscriptionDialogProps) {
  const [contacts, setContacts] = useState<SubscriptionContactSchema[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [autoRecipients, setAutoRecipients] = useState<
    SubscriptionAutoRecipientSchema[]
  >([]);
  const [autoRecipientsError, setAutoRecipientsError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) return;
    setContactsLoading(true);
    vellumApiSdk
      .listSubscriptionContacts({
        query: {
          company_id: companyId,
          facility_id: typ === "Daily" ? facilityId : undefined,
        },
      })
      .then((res) => {
        setContacts(res.data ?? []);
      })
      .catch(() => {
        setContactsError("Something went wrong loading contacts.");
      })
      .finally(() => {
        setContactsLoading(false);
      });
  }, [isOpen, typ, companyId, facilityId]);

  useEffect(() => {
    if (!isOpen) return;
    vellumApiSdk
      .listSubscriptionAutoRecipients({
        query: {
          type: typ,
          company_id: companyId,
          facility_id: typ === "Daily" ? facilityId : undefined,
        },
      })
      .then((res) => {
        setAutoRecipients(res.data ?? []);
      })
      .catch(() => {
        setAutoRecipientsError(
          "Something went wrong loading automatic recipients.",
        );
      });
  }, [isOpen, typ, companyId, facilityId]);

  const contactOptions: SelectOption[] = contacts.map((contact) => ({
    label: `${contact.name} (${contact.role}) • ${contact.email}`,
    value: contact.email,
  }));

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        subscription ? `Edit ${typ} Subscription` : `Create ${typ} Subscription`
      }
      icon="iconoir-mail"
      iconColor="blue"
    >
      <p className="mt-1 text-sm text-gray-500">
        Reports are emailed at the selected hour in US Central Time (CT).
      </p>
      <div className="mt-4">
        <Formik<FormValues>
          initialValues={{
            to_emails: subscription?.to_emails ?? [],
            send_hour: subscription?.send_hour ?? 8,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setStatus(null);
            const body = {
              to_emails: values.to_emails,
              typ,
              company_id: companyId,
              facility_id: typ === "Daily" ? facilityId : null,
              metabase_report_id: String(metabaseReportId),
              send_delay_days: SEND_DELAY_DAYS[typ],
              send_hour: values.send_hour,
            };
            try {
              const res = subscription
                ? await vellumApiSdk.updateSubscription({
                    path: { subscription_id: subscription.id },
                    body,
                  })
                : await vellumApiSdk.createSubscription({ body });
              if (res.data) {
                onSaved(res.data);
                onClose();
              } else {
                setStatus(res.error.detail ?? "Failed to save subscription");
              }
            } catch {
              setStatus("Failed to save subscription");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            isSubmitting,
            status,
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form className="space-y-4">
              <div>
                <Select
                  id="to_emails"
                  name="to_emails"
                  label={`${typ === "Daily" ? "Facility" : "Company"} Recipients`}
                  isMulti
                  isLoading={contactsLoading}
                  options={contactOptions}
                  value={contactOptions.filter((option) =>
                    values.to_emails.includes(option.value),
                  )}
                  onChange={(selected: SelectOption[] | null) => {
                    setFieldValue(
                      "to_emails",
                      (selected ?? []).map((option) => option.value),
                    );
                    setFieldTouched("to_emails", true, false);
                  }}
                  placeholder="Search contacts..."
                  className="max-w-md"
                />
                {contactsError && (
                  <div className="mt-1 ml-1 text-error text-sm">
                    {contactsError}
                  </div>
                )}
                {errors.to_emails && touched.to_emails && (
                  <div className="mt-1 ml-1 text-error text-sm">
                    {typeof errors.to_emails === "string"
                      ? errors.to_emails
                      : "Invalid recipient"}
                  </div>
                )}
                {autoRecipientsError && (
                  <div className="mt-1 ml-1 text-error text-sm">
                    {autoRecipientsError}
                  </div>
                )}
                {autoRecipients.length > 0 && (
                  <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm text-gray-600">
                      These users will automatically be sent this report{" "}
                      {typ.toLowerCase()} as well:
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {autoRecipients.map((recipient) => (
                        <li
                          key={recipient.email}
                          className="text-sm text-gray-700"
                        >
                          {recipient.name} • {recipient.role}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <Select
                  id="send_hour"
                  name="send_hour"
                  label="Send Time (Central Time)"
                  options={sendHourOptions}
                  value={
                    sendHourOptions.find(
                      (option) => option.value === values.send_hour,
                    ) ?? null
                  }
                  onChange={(selected: { value: number } | null) => {
                    setFieldValue("send_hour", selected?.value ?? 8);
                    setFieldTouched("send_hour", true, false);
                  }}
                  placeholder="Select time..."
                  className="max-w-md"
                />
                {errors.send_hour && touched.send_hour && (
                  <div className="mt-1 ml-1 text-error text-sm">
                    {errors.send_hour}
                  </div>
                )}
              </div>
              {status && <Alert>{status}</Alert>}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {subscription ? "Save Changes" : "Create Subscription"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Dialog>
  );
}
